import axios from 'axios';
import { useAuthStore } from '@/store/auth.store';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';

// HttpOnly cookies are the only auth transport on web — JWTs no longer
// touch JavaScript, so XSS can't steal them. The browser auto-sends
// `logixa_access` and `logixa_refresh` because of withCredentials:true and the
// API mounts cookieParser. Mobile clients still use Bearer headers via
// their own native HTTP layer.
const client = axios.create({ baseURL: BASE_URL, timeout: 15000, withCredentials: true });

// Auto-redirect on 401 (expired/revoked token).
client.interceptors.response.use(
  r => r,
  async err => {
    if (typeof window !== 'undefined') {
      const status = err.response?.status;
      if (status === 401) {
        try {
          localStorage.removeItem('logixa_token');
          localStorage.removeItem('logixa-auth');
        } catch {/* private mode */}
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        // 401: fall through to throw the backend's real error message. Do NOT
        // return — that resolves the request with undefined and api.post(...).data
        // throws "Cannot read properties of undefined (reading 'data')".
      }
      if (status === 403 && err.response?.data?.message === 'CONSENT_REQUIRED') {
        const { user, setAuth } = useAuthStore.getState();
        if (user && !user.needsReconsent) {
          setAuth(null, null, { ...user, needsReconsent: true });
        }
        if (!window.location.pathname.startsWith('/consent')) {
          window.location.href = '/consent';
        }
        return;
      }
    }
    const msg = err.response?.data?.message;
    throw new Error(Array.isArray(msg) ? msg.join(', ') : msg || err.message);
  },
);

export const api = {
  get: async <T = any>(url: string): Promise<T> => (await client.get(url)).data,
  post: async <T = any>(url: string, data?: any): Promise<T> => (await client.post(url, data)).data,
  patch: async <T = any>(url: string, data?: any): Promise<T> => (await client.patch(url, data)).data,
  delete: async <T = any>(url: string): Promise<T> => (await client.delete(url)).data,
};
